import React from 'react';
import { Shuffle } from 'lucide-react';
import { useTheme, type Theme } from '@/contexts/ThemeContext';
import { cn } from '@/lib/utils';

// Define curated themes (excluding light/dark which are handled by theme toggle)
const CURATED_THEMES: Theme[] = [
  'forest',
  'ocean',
  'cherry',
  'sunset',
  'midnight',
  'grape',
  'mint',
  'volcano',
  'galaxy',
  'autumn',
  'arctic',
  'tropical',
];

interface ThemeRandomizerProps {
  className?: string;
  size?: 'sm' | 'md' | 'lg';
  variant?: 'default' | 'outline' | 'ghost';
}

export const ThemeRandomizer: React.FC<ThemeRandomizerProps> = ({
  className,
  size = 'md',
  variant = 'ghost',
}) => {
  const { theme, setTheme } = useTheme();

  const sizeClasses = {
    sm: 'h-8 w-8',
    md: 'h-10 w-10',
    lg: 'h-12 w-12',
  };

  const variantClasses = {
    default: 'bg-primary text-primary-foreground hover:bg-primary/90',
    outline:
      'border border-input bg-background hover:bg-accent hover:text-accent-foreground',
    ghost: 'hover:bg-accent hover:text-accent-foreground',
  };

  const randomizeTheme = () => {
    // Get a random theme from curated themes
    const randomIndex = Math.floor(Math.random() * CURATED_THEMES.length);
    const randomTheme = CURATED_THEMES[randomIndex];

    // Only set if it's different from current theme
    if (randomTheme !== theme) {
      setTheme(randomTheme);
    } else {
      // If we got the same theme, try the next one in the array
      const nextIndex = (randomIndex + 1) % CURATED_THEMES.length;
      setTheme(CURATED_THEMES[nextIndex]);
    }
  };

  return (
    <button
      onClick={randomizeTheme}
      className={cn(
        'inline-flex items-center justify-center rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:opacity-50 disabled:pointer-events-none ring-offset-background',
        sizeClasses[size],
        variantClasses[variant],
        className
      )}
      aria-label='Randomize theme'
      title='Randomize theme'
    >
      {/* Shuffle/Randomize icon */}
      <Shuffle
        className={cn(
          'transition-all',
          size === 'sm' ? 'h-4 w-4' : size === 'lg' ? 'h-6 w-6' : 'h-5 w-5'
        )}
      />
    </button>
  );
};

export default ThemeRandomizer;
